import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { Chip } from 'tio-ui/components/buttons';
import { PhCheckCircle, PhXCircle, PhWarning } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format';

/**
 * Recommendation Card
 * Displays the main AI recommendation with confidence score and calculation details
 */
export interface RecommendationCardSignature {
  Args: {
    recommendation: 'APPROVE' | 'DENY' | 'REQUEST_MORE_INFO' | string;
    confidenceScore?: number;
    calculationDetails?: string;
    approvedAmount?: number;
    requestedAmount?: number;
  };
}

/**
 * Format recommendation enum value to human-readable text
 */
function formatRecommendation(rec: string): string {
  const formattedMap: Record<string, string> = {
    approved_for_requested_amount: 'Approved for Requested Amount',
    approved_for_less_than_requested: 'Approved for Less Than Requested',
    approved_for_partial_amount: 'Approved for Partial Amount',
    denied: 'Denied',
    needs_more_information: 'Needs More Information',
    APPROVE: 'Approve',
    DENY: 'Deny',
    REQUEST_MORE_INFO: 'Request More Info',
  };

  return formattedMap[rec] || rec.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const RecommendationCard: TOC<RecommendationCardSignature> = <template>
  <div class="bg-purple-50 border border-purple-100 rounded-lg p-4">
    <div class="flex items-start gap-3 mb-3">
      {{#if (eq @recommendation "APPROVE")}}
        <PhCheckCircle @size="24" class="text-green-600 flex-shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="success" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else if (eq @recommendation "DENY")}}
        <PhXCircle @size="24" class="text-red-600 flex-shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="danger" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else if (eq @recommendation "REQUEST_MORE_INFO")}}
        <PhWarning @size="24" class="text-yellow-600 flex-shrink-0 mt-0.5" />
        <div class="flex-1">
          <Chip @intent="warning" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{else}}
        <div class="flex-1">
          <Chip @intent="default" @size="sm">{{formatRecommendation @recommendation}}</Chip>
        </div>
      {{/if}}

      {{#if @confidenceScore}}
        <div class="text-right">
          <p class="text-xs text-gray-500">Confidence Score</p>
          <p class="text-lg font-semibold text-purple-700">{{@confidenceScore}}%</p>
        </div>
      {{/if}}
    </div>

    {{! Financial Summary }}
    {{#if @approvedAmount}}
      <div class="mt-3 pt-3 border-t border-purple-200">
        <div class="flex items-center justify-between text-sm">
          {{#if @requestedAmount}}
            <div class="flex items-center gap-1.5">
              <span class="text-gray-600">Requested:</span>
              <span class="font-semibold text-gray-800">{{formatAsDollars
                  @requestedAmount
                  (hash cents=false)
                }}</span>
            </div>
          {{/if}}
          <div class="flex items-center gap-1.5">
            <span class="text-gray-600">Approved:</span>
            <span class="font-semibold text-green-700">{{formatAsDollars
                @approvedAmount
                (hash cents=false)
              }}</span>
          </div>
        </div>
      </div>
    {{/if}}

    {{! Calculation Details }}
    {{#if @calculationDetails}}
      <div class="mt-3 pt-3 border-t border-purple-200">
        <p class="text-sm font-semibold text-gray-700 mb-1">Calculation Details:</p>
        <p class="text-sm text-gray-600 leading-relaxed">{{@calculationDetails}}</p>
      </div>
    {{/if}}
  </div>
</template>;

export default RecommendationCard;
